// src/App.tsx
import { BrowserRouter as Router } from 'react-router-dom';
import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import RouteConfig from './routes/RouteConfig';
import { AuthProvider } from './context/AuthContext';
import { TradeProvider } from './context/TradeContext';
import { NoteProvider } from './context/NoteContext';
import { NetworkProvider } from './context/NetworkContext';
import { ThemeProvider } from './context/ThemeContext';
import { LayoutProvider } from './context/LayoutContext';
import { NetworkStatus } from './components/NetworkStatus';
import ErrorBoundary from './components/ErrorBoundary';
import ScrollFix from './components/ScrollFix';
import { MobileWrapper } from './components/MobileWrapper';
import { SplashScreen } from '@capacitor/splash-screen';
import { initSentry } from './lib/sentry';

// Initialize Sentry
initSentry();

// Premium scroll initialization function
const initializePremiumScroll = () => {
  const anchorClickHandlers = new Map<Element, EventListener>();
  
  // Add smooth scroll to all anchor links with null safety
  document.querySelectorAll('a[href^="#"]').forEach(anchor => {
    const handleClick = function (e: Event) {
      e.preventDefault();
      const href = anchor.getAttribute('href');
      if (!href) return;
      
      const target = document.querySelector(href);
      if (target) {
        target.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    };
    
    anchor.addEventListener('click', handleClick);
    anchorClickHandlers.set(anchor, handleClick);
  });

  // Add scroll padding for better positioning
  document.documentElement.style.scrollPaddingTop = '2rem';
  
  // Enhanced keyboard navigation with cleanup
  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Tab') {
      // Ensure focused elements are properly scrolled into view
      setTimeout(() => {
        const focused = document.activeElement as HTMLElement;
        if (focused?.scrollIntoView) {
          focused.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
          });
        }
      }, 0);
    }
  };
  
  document.addEventListener('keydown', handleKeyDown);

  // Return cleanup function
  return () => {
    // Clean up anchor click listeners
    anchorClickHandlers.forEach((handler, anchor) => {
      anchor.removeEventListener('click', handler);
    });
    anchorClickHandlers.clear();
    
    // Clean up keyboard listener
    document.removeEventListener('keydown', handleKeyDown);
    
    // Reset scroll padding
    document.documentElement.style.scrollPaddingTop = '';
  };
};

export default function App() {
  console.log('App: Rendering App component');
  console.log('VITE_SUPABASE_URL:', import.meta.env.VITE_SUPABASE_URL);

  useEffect(() => {
    // Handle wallet extension errors
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      // Check if error is from wallet extension
      if (event.reason && typeof event.reason === 'object') {
        const error = event.reason;
        if (error.code === 4001 || 
            (error.message && error.message.includes('wallet')) ||
            (error.message && error.message.includes('account'))) {
          console.log('🦊 Ignoring wallet extension error:', error);
          event.preventDefault(); // Prevent the error from showing
          return;
        }
      }
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    // Initialize premium scroll experience with cleanup
    const cleanup = initializePremiumScroll();

    // Hide splash screen when the app is ready
    // This is a fallback in case MobileWrapper doesn't handle it
    try {
      SplashScreen.hide();
    } catch (error) {
      console.error('Error hiding splash screen:', error);
    }

    // Return cleanup function to remove all event listeners
    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      cleanup();
    };
  }, []);

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <LayoutProvider>
          <MobileWrapper>
            <ScrollFix />
            <Router>
              <AuthProvider>
                <NetworkProvider>
                  <TradeProvider>
                    <NoteProvider>
                      <RouteConfig />
                      <NetworkStatus />
                    </NoteProvider>
                  </TradeProvider>
                </NetworkProvider>
              </AuthProvider>
            </Router>
          </MobileWrapper>
        </LayoutProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}